import ApiService from "../Services/ApiService";

/**
 * P3 : espace documentaire unifié.
 * Représentation homogène renvoyée par le backend pour tous les types
 * de documents (devis, factures, attestations, abonnements transport).
 */
export interface DocumentInfo {
    /** Identifiant stable et unique préfixé par le type (ex. "invoice-123"), utilisable comme clé React. */
    id?: string;
    type: "quote" | "invoice" | "invoice_recap" | "travel_attestation" | "deposit_attestation" | "tax_credit" | "transport";
    /** Catégorie d'affichage : quote, invoice (récapitulatifs inclus), attestation, tax_credit, transport. */
    category?: string;
    label: string;
    number: string | null;
    date: string | null;
    amount: number | null;
    courseId: number | null;
    /** quote : active | replaced ; travel_attestation : valid | invalidated ; deposit_attestation : valid | superseded ; transport : valid | to_confirm | expired ; sinon null. */
    status: string | null;
    url: string;
    /**
     * Client (parent) du document : devis, factures et attestations de
     * déplacement. null pour les abonnements transport (propres au prof) et
     * pour l'attestation fiscale.
     */
    clientId?: number | null;
    clientName?: string | null;
    /** Élève du cours, quand le cours a été pris pour un enfant. */
    childName?: string | null;
    /** Période de validité (YYYY-MM-DD), abonnements transport uniquement. */
    validFrom?: string | null;
    validUntil?: string | null;
    /** Signé : négatif quand l'abonnement est déjà expiré. Abonnements transport uniquement. */
    expiresInDays?: number | null;
    /** Explication du statut, par exemple le motif d'invalidation d'une attestation. */
    statusReason?: string | null;
    /** Identifiant de l'abonnement transport (confirmation depuis la liste). */
    subscriptionId?: number | null;
}

/** Filtres serveur de la liste prof (égalité stricte côté backend). */
export interface TeachrDocumentFilters {
    /** Identifiant du client : seuls ses documents sont renvoyés (jamais les abonnements transport). */
    client?: number;
    category?: string;
    status?: string;
}

export default abstract class DocumentRepository {
    /** Tous les documents du professeur connecté, éventuellement filtrés côté serveur. */
    public static getTeachrDocuments(teachrId: number, filters?: TeachrDocumentFilters): Promise<{ documents: DocumentInfo[] }> {
        const params = new URLSearchParams();
        if (filters?.client !== undefined) params.set("client", String(filters.client));
        if (filters?.category) params.set("category", filters.category);
        if (filters?.status) params.set("status", filters.status);
        const query = params.toString();
        return ApiService.get(`/teachrs/v2/teachrs/${teachrId}/documents${query ? `?${query}` : ""}`) as Promise<{ documents: DocumentInfo[] }>;
    }

    /** Tous les documents du client connecté. */
    public static getClientDocuments(clientId: number): Promise<{ documents: DocumentInfo[] }> {
        return ApiService.get(`/clients/v2/clients/${clientId}/documents`) as Promise<{ documents: DocumentInfo[] }>;
    }
}
